import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

import exercisesInitialStorage from './exercisesInitialStorage';
import setCurrentExercise from '../_actions/setCurrentExercise';
import setCurriculum from '../_actions/setCurriculum';
import setCursor from '../_actions/setCursor';
import getExercises, { parseVariables } from '../_helpers/getExercises';

const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';

/**
 * Apply list response. Preserve filter fields already set on the store;
 * only refresh exercise list / cursor (same end state as pre-migration `set(exercises)`
 * after the initial storage wipe, without blanking the UI mid-request).
 */
const applyExercisesResult = (set, result) => {
  const list = result?.exercises ?? null;
  set({
    ...result,
    exerciseCursor: 0,
    currentExercise: list?.[0] ?? null,
    currentExerciseShape: { t: 0, o: 0, p: 0, a: 0 },
    fileType: 't',
    mark: null,
    allExercisesCompleted: result?.allExercisesCompleted ?? false,
  });
};

const store = persist(
  (set, get) => ({
    ...exercisesInitialStorage,
    fetched: false,

    // ACTIONS
    setExercisesAfterSubjectSelect: async subject => {
      const variables = parseVariables(get(), {
        ...exercisesInitialStorage,
        subject,
      });
      const exercises = await getExercises(variables);
      set({ ...exercisesInitialStorage, ...exercises });
    },
    setSection: async section => {
      // Pre-migration: wipe filters/curriculum; keep orderBy + userMarks.
      set(state => ({
        ...exercisesInitialStorage,
        orderBy: state.orderBy,
        userMarks: state.userMarks,
        section,
      }));
      const variables = parseVariables(get(), { section });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setSubsection: async subsection => {
      // Pre-migration: wipe filters/curriculum; keep section, orderBy, userMarks.
      set(state => ({
        ...exercisesInitialStorage,
        orderBy: state.orderBy,
        userMarks: state.userMarks,
        section: state.section,
        subsection,
      }));
      const variables = parseVariables(get(), { subsection });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setSectionAndSubsection: async ({ section, subsection }) => {
      set({
        ...exercisesInitialStorage,
        section: section || null,
        subsection: subsection || null,
      });
      const variables = parseVariables(get(), { subsection, section });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setFilters: async filters => {
      // Pre-migration: keep section/subsection/orderBy/curriculum/userMarks; replace tags.
      set(state => ({
        ...exercisesInitialStorage,
        section: state.section,
        subsection: state.subsection,
        orderBy: state.orderBy,
        curriculum: state.curriculum,
        userMarks: state.userMarks,
        filters,
        // Keep current list visible until the new fetch arrives (checkbox stays instant).
        exercises: state.exercises,
        exercisesCount: state.exercisesCount,
        fetched: state.fetched,
        subjectExercisesCount: state.subjectExercisesCount,
      }));
      const variables = parseVariables(get(), { filters });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setUserMarks: async userMarks => {
      // Pre-migration: keep section/subsection/orderBy/curriculum; clear tag filters.
      set(state => ({
        ...exercisesInitialStorage,
        section: state.section,
        subsection: state.subsection,
        orderBy: state.orderBy,
        curriculum: state.curriculum,
        userMarks,
        exercises: state.exercises,
        exercisesCount: state.exercisesCount,
        fetched: state.fetched,
        subjectExercisesCount: state.subjectExercisesCount,
      }));
      const variables = parseVariables(get(), { userMarks });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    setOrder: async orderBy => {
      // Pre-migration: keep section/subsection/filters/curriculum; reset userMarks to default.
      set(state => ({
        ...exercisesInitialStorage,
        section: state.section,
        subsection: state.subsection,
        filters: state.filters,
        curriculum: state.curriculum,
        orderBy,
        exercises: state.exercises,
        exercisesCount: state.exercisesCount,
        fetched: state.fetched,
        subjectExercisesCount: state.subjectExercisesCount,
      }));
      const variables = parseVariables(get(), { orderBy });
      const exercises = await getExercises(variables);
      applyExercisesResult(set, exercises);
    },
    reload: async () => {
      const state = get();
      const variables = parseVariables(state);
      set({ exercises: null, currentExercise: null });
      const { exercises } = await getExercises(variables);
      const currentExercise = exercises[state.exerciseCursor];
      set({ exercises, currentExercise });
    },
    setExerciseMark: mark => {
      set({ mark });
    },
    reset: () => set({ ...exercisesInitialStorage, fetched: false }),
    setFileType: fileType => set({ fileType }),
    setTotalCount: subjectExercisesCount => set({ subjectExercisesCount }),
    setCurriculum: setCurriculum(set, get),
    setExerciseCursor: setCursor(set, get),
    setCurrentExercise: setCurrentExercise(set),
  }),
  {
    name: `${import.meta.env.VITE_VERSION}_exercises`,
    getStorage: () => sessionStorage,
  },
);

const useExercisesStore = create(devtools(store, { name: 'EXERCISES', enabled: !isProduction }));

export default useExercisesStore;
